import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import {
  Users, Calendar, Target, Plus, Search, ChevronRight, CheckCircle, Video, Settings,
  Mail, Bell, UserPlus, Shield, Send, Loader2, X, Trash2, Ban, RotateCcw,
  TrendingUp, FileText, Eye, ExternalLink, Briefcase, ClipboardList, Edit2, MoreVertical, ClipboardCheck, Pencil
} from "lucide-react";
import { toast } from "sonner";
import { API, authFetch } from "@/App";

export function ClientListView({
  user,
  clients,
  filteredClients,
  searchQuery,
  setSearchQuery,
  onboardedClients,
  pendingInvites,
  pendingClients,
  upgradeRequests,
  pendingIntakes,
  upcomingMeetings,
  handleUpdateMeetingStatus,
  events,
  teamMembers,
  // Add Client Dialog
  showAddClientDialog,
  setShowAddClientDialog,
  addClientForm,
  setAddClientForm,
  sendingInvite,
  sendingIntake,
  handleInviteClient,
  handleSendClientIntake,
  // Invite handlers
  handleSendReminderOld,
  sendingReminder,
  handleCancelPendingInvite,
  handleClearAllPendingInvites,
  // Pending client handlers
  handleApproveClient,
  approvingClient,
  // Upgrade handlers
  handleProcessUpgrade,
  processingUpgrade,
  // Intake handlers
  handleApproveIntake,
  handleDenyIntake,
  processingIntake,
  // Client action handlers
  handleUpdateClientTier,
  handleManualOnboard,
  handleSuspendClient,
  handleRestoreClient,
  handleGenerateSponsorReport,
  generatingReport,
  processingAction,
  setSelectedClientForPrograms,
  setShowProgramsDialog,
  setSendIntakeClient,
  setSelectedClientForAssign,
  setShowAssignDialog,
  setClientToDelete,
  setShowDeleteClientDialog,
  // Tier helpers
  getTierLabel,
  getTierBadgeStyle,
  // View mode
  setClientViewMode,
  // Meeting/Event/Settings
  setShowMeetingDialog,
  setShowWorkshopDialog,
  setShowSettingsDialog,
  // Team management
  setShowAddConsultantDialog,
  setConsultantToDelete,
  setShowDeleteConsultantDialog,
  // Events dialog
  setShowEventsDialog
}) {
  const navigate = useNavigate();
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [editForm, setEditForm] = useState({ date: "", time: "", title: "", duration_minutes: 60 });
  const [savingEdit, setSavingEdit] = useState(false);

  const handleSaveReschedule = async () => {
    if (!editingMeeting) return;
    setSavingEdit(true);
    try {
      const res = await authFetch(`${API}/consultant/meetings/${editingMeeting}`, {
        method: "PUT",
        body: JSON.stringify(editForm),
      });
      if (res.ok) {
        toast.success("Meeting rescheduled");
        setEditingMeeting(null);
        if (typeof handleUpdateMeetingStatus === "function") handleUpdateMeetingStatus(editingMeeting, "scheduled");
        else window.location.reload();
      } else toast.error("Failed to update");
    } catch { toast.error("Failed"); }
    setSavingEdit(false);
  };

  return (
    <>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Clients List */}
      <div className="lg:col-span-2">
        <Card className="border-neutral-200">
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2">
                <CardTitle className="text-lg font-semibold">Clients</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setClientViewMode('pipeline')}
                  className="h-7 text-xs"
                >
                  <Target className="w-3 h-3 mr-1" />
                  Pipeline
                </Button>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                <div className="relative w-full sm:w-48">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <Input
                    placeholder="Search clients..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-9"
                    data-testid="client-search-input"
                  />
                </div>
                <Dialog open={showAddClientDialog} onOpenChange={setShowAddClientDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-[#39FF14] hover:bg-[#32E010] whitespace-nowrap" data-testid="add-client-btn-list">
                      <UserPlus className="w-4 h-4 mr-1" />
                      Add Client
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Invite New Client</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      {/* Invite Type Selection */}
                      <div className="space-y-2">
                        <Label>Invitation Type</Label>
                        <Select
                          value={addClientForm.invite_type}
                          onValueChange={(value) => setAddClientForm({ ...addClientForm, invite_type: value })}
                        >
                          <SelectTrigger data-testid="add-client-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="email">Send Direct Invitation</SelectItem>
                            <SelectItem value="intake">Send Intake Form First</SelectItem>
                          </SelectContent>
                        </Select>
                        {addClientForm.invite_type === "intake" && (
                          <p className="text-xs text-blue-600">
                            Client will complete an intake form. You can review and approve before granting dashboard access.
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Client Name *</Label>
                        <Input
                          value={addClientForm.name}
                          onChange={(e) => setAddClientForm({ ...addClientForm, name: e.target.value })}
                          placeholder="Enter client's full name"
                          data-testid="add-client-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email Address *</Label>
                        <Input
                          type="email"
                          value={addClientForm.email}
                          onChange={(e) => setAddClientForm({ ...addClientForm, email: e.target.value })}
                          placeholder="client@example.com"
                          data-testid="add-client-email-input"
                        />
                      </div>
                      {/* Only show tier for direct invitations */}
                      {addClientForm.invite_type === "email" && (
                      <div className="space-y-2">
                        <Label>Assign Tier</Label>
                        <Select
                          value={addClientForm.tier}
                          onValueChange={(value) => setAddClientForm({ ...addClientForm, tier: value })}
                        >
                          <SelectTrigger data-testid="add-client-tier-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="basic">Basic Consulting</SelectItem>
                            <SelectItem value="premium_rapid">R.A.P.I.D.™ Program</SelectItem>
                            <SelectItem value="podcast_academy">Podcast Launch Academy</SelectItem>
                            <SelectItem value="growth_strategic">Strategic Foundation ($15k)</SelectItem>
                            <SelectItem value="growth_optimization">Growth & Optimization ($25k)</SelectItem>
                            <SelectItem value="growth_scale">Scale & Leadership ($40k)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      )}
                      {/* Custom message for intake */}
                      {addClientForm.invite_type === "intake" && (
                      <div className="space-y-2">
                        <Label>Custom Message (Optional)</Label>
                        <Textarea
                          value={addClientForm.intake_message}
                          onChange={(e) => setAddClientForm({ ...addClientForm, intake_message: e.target.value })}
                          placeholder="Add a personalized message..."
                          rows={2}
                        />
                      </div>
                      )}
                      <div className={`p-3 rounded-lg text-sm ${addClientForm.invite_type === "intake" ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-700"}`}>
                        {addClientForm.invite_type === "intake" ? (
                          <>
                            <ClipboardList className="w-4 h-4 inline mr-2" />
                            Client will receive an intake form to complete. You'll review their submission before approving access.
                          </>
                        ) : (
                          <>
                            <Mail className="w-4 h-4 inline mr-2" />
                            An invitation email will be sent with a link to set up their account
                          </>
                        )}
                      </div>
                      <Button 
                        onClick={addClientForm.invite_type === "intake" ? handleSendClientIntake : handleInviteClient} 
                        disabled={sendingInvite || sendingIntake || !addClientForm.name || !addClientForm.email}
                        className="w-full bg-[#39FF14] hover:bg-[#32E010]"
                        data-testid="send-invite-btn"
                      >
                        {(sendingInvite || sendingIntake) ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : addClientForm.invite_type === "intake" ? (
                          <>
                            <ClipboardList className="w-4 h-4 mr-2" />
                            Send Intake Form
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Send Invitation
                          </>
                        )}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            {/* Pending Invites */}
            {pendingInvites.length > 0 && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-amber-800">
                    Pending Invitations ({pendingInvites.length})
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={handleClearAllPendingInvites}
                    title="Clear all pending invitations"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Clear All
                  </Button>
                </div>
                <div className="space-y-2">
                  {pendingInvites.slice(0, 3).map((invite) => (
                    <div key={invite.invite_id} className="flex items-center justify-between text-sm">
                      <div className="flex-1">
                        <span className="text-amber-700">{invite.name} ({invite.email})</span>
                        {invite.reminder_count > 0 && (
                          <Badge variant="outline" className="ml-2 text-xs">
                            {invite.reminder_count} reminder{invite.reminder_count > 1 ? 's' : ''} sent
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs text-amber-600 hover:text-amber-800 hover:bg-amber-100"
                          onClick={() => handleSendReminderOld(invite.invite_id)}
                          disabled={sendingReminder === invite.invite_id}
                          title="Send reminder email"
                          data-testid={`remind-btn-${invite.invite_id}`}
                        >
                          {sendingReminder === invite.invite_id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Bell className="w-3 h-3" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleCancelPendingInvite(invite.invite_id)}
                          title="Cancel invitation"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Pending Client Approvals */}
            {pendingClients.length > 0 && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-800 mb-2">
                  Awaiting Approval ({pendingClients.length})
                </p>
                <div className="space-y-2">
                  {pendingClients.slice(0, 5).map((client) => (
                    <div key={client.user_id} className="flex items-center justify-between text-sm bg-white p-2 rounded border border-blue-100">
                      <div className="flex-1">
                        <span className="font-medium text-blue-900">{client.name}</span>
                        <span className="text-blue-600 ml-2">({client.email})</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Select onValueChange={(program) => handleApproveClient(client.user_id, program)}>
                          <SelectTrigger className="h-7 w-32 text-xs" data-testid={`approve-select-${client.user_id}`}>
                            <SelectValue placeholder="Approve as..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="basic">Basic</SelectItem>
                            <SelectItem value="premium_rapid">R.A.P.I.D.™</SelectItem>
                            <SelectItem value="podcast_academy">Podcast Academy</SelectItem>
                            <SelectItem value="growth_strategic">Strategic Foundation</SelectItem>
                            <SelectItem value="growth_optimization">Growth & Optimization</SelectItem>
                            <SelectItem value="growth_scale">Scale & Leadership</SelectItem>
                          </SelectContent>
                        </Select>
                        {approvingClient === client.user_id && (
                          <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Upgrade Requests Section */}
            {upgradeRequests.length > 0 && (
              <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm font-medium text-purple-800 mb-2">
                  <TrendingUp className="w-4 h-4 inline mr-1" />
                  Upgrade Requests ({upgradeRequests.length})
                </p>
                <div className="space-y-2">
                  {upgradeRequests.slice(0, 5).map((request) => (
                    <div key={request.request_id} className="flex items-center justify-between text-sm bg-white p-3 rounded border border-purple-100">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-purple-900">{request.client_name}</span>
                          <ChevronRight className="w-3 h-3 text-purple-400" />
                          <Badge className="bg-purple-100 text-purple-700 text-xs">
                            {request.requested_tier_name}
                          </Badge>
                        </div>
                        <p className="text-xs text-purple-600 mt-1">{request.client_email}</p>
                        {request.message && (
                          <p className="text-xs text-purple-500 mt-1 italic">"{request.message}"</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button 
                          size="sm" 
                          className="h-7 text-xs bg-green-600 hover:bg-green-700"
                          onClick={() => handleProcessUpgrade(request.request_id, 'approve')}
                          disabled={processingUpgrade === request.request_id}
                          data-testid={`approve-upgrade-${request.request_id}`}
                        >
                          {processingUpgrade === request.request_id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <CheckCircle className="w-3 h-3 mr-1" />
                          )}
                          Approve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => handleProcessUpgrade(request.request_id, 'decline')}
                          disabled={processingUpgrade === request.request_id}
                          data-testid={`decline-upgrade-${request.request_id}`}
                        >
                          <X className="w-3 h-3 mr-1" />
                          Decline
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pending Intakes Section */}
            {pendingIntakes.length > 0 && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-800 mb-2">
                  <ClipboardList className="w-4 h-4 inline mr-1" />
                  Pending Intake Forms ({pendingIntakes.length})
                </p>
                <div className="space-y-2">
                  {pendingIntakes.slice(0, 5).map((intake) => (
                    <div key={intake.intake_id} className="flex items-center justify-between text-sm bg-white p-3 rounded border border-blue-100" data-testid={`intake-row-${intake.intake_id}`}>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-blue-900">{intake.user_info?.name || intake.client_name || "Unknown"}</span>
                          <Badge className={`text-xs ${intake.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                            {intake.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-blue-600 mt-1">{intake.user_info?.email || intake.client_email}</p>
                        {intake.business_name && (
                          <p className="text-xs text-blue-500 mt-1">Business: {intake.business_name}</p>
                        )}
                        {intake.primary_goal && (
                          <p className="text-xs text-blue-500 mt-1 italic">Goal: {intake.primary_goal}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => navigate(`/consultant/client/${intake.user_id}`)}
                          data-testid={`view-intake-${intake.intake_id}`}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View
                        </Button>
                        <Button 
                          size="sm" 
                          className="h-7 text-xs bg-green-600 hover:bg-green-700"
                          onClick={() => handleApproveIntake(intake.intake_id)}
                          disabled={processingIntake === intake.intake_id}
                          data-testid={`approve-intake-${intake.intake_id}`}
                        >
                          {processingIntake === intake.intake_id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <CheckCircle className="w-3 h-3 mr-1" />
                          )}
                          Approve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => handleDenyIntake(intake.intake_id)}
                          disabled={processingIntake === intake.intake_id}
                          data-testid={`deny-intake-${intake.intake_id}`}
                        >
                          <X className="w-3 h-3 mr-1" />
                          Deny
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {filteredClients.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                <p className="text-neutral-500">No clients found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredClients.map((client) => (
                  <div 
                    key={client.user_id}
                    className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition-colors gap-2"
                    data-testid={`client-row-${client.user_id}`}
                  >
                    {/* Left: Avatar + Name + Email + Badges */}
                    <div 
                      className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                      onClick={() => navigate(`/consultant/client/${client.user_id}`)}
                    >
                      <div className="w-9 h-9 rounded-full bg-[#39FF14]/10 flex items-center justify-center flex-shrink-0">
                        {client.picture ? (
                          <img src={client.picture} alt="" className="w-9 h-9 rounded-full" />
                        ) : (
                          <span className="text-[#39FF14] font-semibold text-sm">
                            {client.name?.charAt(0) || "?"}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="font-medium text-neutral-900 text-sm truncate">{client.name}</p>
                          {client.programs?.includes('podcast_academy') && (
                            <span className="text-xs" title="Podcast Academy">🎙️</span>
                          )}
                          {client.programs?.includes('rapid') && (
                            <span className="text-xs" title="R.A.P.I.D.™">⚡</span>
                          )}
                          {client.downgraded_at && (
                            <span className="text-xs" title="Downgraded">⚠️</span>
                          )}
                        </div>
                        <p className="text-xs text-neutral-500 truncate">{client.email}</p>
                      </div>
                    </div>

                    {/* Right: Tier + Status + Actions Dropdown */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Tier Dropdown */}
                      <Select
                        value={client.tier || "unassigned"}
                        onValueChange={(value) => handleUpdateClientTier(client.user_id, value === "unassigned" ? null : value)}
                      >
                        <SelectTrigger 
                          className={`w-24 h-7 text-xs font-medium border ${getTierBadgeStyle(client.tier)}`}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`tier-select-${client.user_id}`}
                        >
                          <SelectValue placeholder="Tier">{getTierLabel(client.tier)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          <SelectItem value="basic">Basic</SelectItem>
                          <SelectItem value="premium_rapid">R.A.P.I.D.™</SelectItem>
                          <SelectItem value="podcast_academy">Podcast</SelectItem>
                          <SelectItem value="growth_strategic">Foundation</SelectItem>
                          <SelectItem value="growth_optimization">Growth</SelectItem>
                          <SelectItem value="growth_scale">Scale</SelectItem>
                        </SelectContent>
                      </Select>

                      {/* Status Badge */}
                      <Badge 
                        variant={client.onboarded ? "default" : "secondary"}
                        className={`text-xs px-2 py-0.5 ${client.onboarded ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}
                      >
                        {client.onboarded ? "Active" : "Pending"}
                      </Badge>

                      {/* Actions Dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`client-actions-${client.user_id}`}>
                            <MoreVertical className="h-4 w-4 text-neutral-500" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => navigate(`/consultant/client/${client.user_id}`)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Profile
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setSelectedClientForPrograms(client); setShowProgramsDialog(true); }}>
                            <Briefcase className="w-4 h-4 mr-2" />
                            Manage Programs
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setSendIntakeClient(client)}>
                            <ClipboardList className="w-4 h-4 mr-2" />
                            Send Intake Form
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleGenerateSponsorReport(client.user_id, client.name)}
                            disabled={generatingReport === client.user_id}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            {generatingReport === client.user_id ? "Generating..." : "Sponsor Report"}
                          </DropdownMenuItem>
                          {(client.tier === "premium_rapid" || client.tier === "podcast_academy") && (
                            <DropdownMenuItem onClick={() => navigate(`/consultant/rapid/${client.user_id}`)}>
                              <Target className="w-4 h-4 mr-2" />
                              R.A.P.I.D. Dashboard
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => { setSelectedClientForAssign(client); setShowAssignDialog(true); }}>
                            <UserPlus className="w-4 h-4 mr-2" />
                            {client.assigned_consultants_info?.length > 0 ? "Manage Consultants" : "Assign Consultant"}
                          </DropdownMenuItem>
                          {!client.onboarded && (
                            <DropdownMenuItem 
                              onClick={() => handleManualOnboard(client.user_id, client.name)}
                              disabled={processingAction === client.user_id}
                              className="text-green-600"
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Manual Onboard
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {client.access_status === "suspended" ? (
                            <DropdownMenuItem 
                              onClick={() => handleRestoreClient(client.user_id)}
                              disabled={processingAction === client.user_id}
                              className="text-green-600"
                            >
                              <RotateCcw className="w-4 h-4 mr-2" />
                              Restore Access
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem 
                              onClick={() => handleSuspendClient(client.user_id)}
                              disabled={processingAction === client.user_id}
                              className="text-amber-600"
                            >
                              <Ban className="w-4 h-4 mr-2" />
                              Suspend Access
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            onClick={() => { setClientToDelete(client); setShowDeleteClientDialog(true); }}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Client
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sidebar - Meetings & Events */}
      <div className="space-y-6">
        {/* Upcoming Meetings */}
        <Card className="border-neutral-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold flex items-center">
              <Video className="w-5 h-5 mr-2 text-blue-500" />
              Scheduled Meetings
            </CardTitle>
          </CardHeader>
          <CardContent>
            {upcomingMeetings.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-neutral-500 text-sm mb-3">No meetings scheduled</p>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMeetingDialog(true)}
                  className="border-blue-200 text-blue-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Schedule Meeting
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingMeetings.slice(0, 10).map((meeting) => {
                  const statusColors = {
                    scheduled: "bg-blue-50 border-blue-100",
                    checked_in: "bg-emerald-50 border-emerald-100",
                    completed: "bg-green-50 border-green-200",
                    cancelled: "bg-red-50 border-red-100 opacity-60",
                  };
                  const badgeColors = {
                    scheduled: "text-blue-700 border-blue-200 bg-blue-50",
                    checked_in: "text-emerald-700 border-emerald-200 bg-emerald-50",
                    completed: "text-green-700 border-green-200 bg-green-50",
                    cancelled: "text-red-700 border-red-200 bg-red-50",
                  };
                  const status = meeting.status || "scheduled";
                  return (
                    <div key={meeting.meeting_id} className={`p-3 rounded-lg border ${statusColors[status] || statusColors.scheduled}`}>
                      <div className="flex items-start justify-between mb-2">
                        <p className="font-medium text-neutral-900 text-sm">{meeting.title}</p>
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className={`text-xs ${badgeColors[status] || ""}`}>
                            {status.replace("_", " ")}
                          </Badge>
                          <Badge variant="outline" className="text-xs text-neutral-500 border-neutral-200">
                            {meeting.meeting_type}
                          </Badge>
                        </div>
                      </div>
                      <p className="text-xs text-neutral-500 mb-1">
                        with {meeting.client?.name || "Client"}
                      </p>
                      <div className="flex items-center text-xs text-neutral-600 space-x-2 mb-2">
                        <span>{meeting.date}</span>
                        <span>•</span>
                        <span>{meeting.time}</span>
                        <span>•</span>
                        <span>{meeting.duration_minutes}min</span>
                      </div>
                      {/* Action Buttons */}
                      {status !== "completed" && status !== "cancelled" && handleUpdateMeetingStatus && (
                        <div className="flex gap-2 pt-1 border-t border-neutral-200/50">
                          {status === "scheduled" && (
                            <Button size="sm" variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50 h-7 text-xs"
                              onClick={() => {
                                setEditForm({ date: meeting.date || "", time: meeting.time || "", title: meeting.title || "", duration_minutes: meeting.duration_minutes || 60 });
                                setEditingMeeting(meeting.meeting_id);
                              }}
                              data-testid={`edit-${meeting.meeting_id}`}>
                              <Pencil className="w-3 h-3 mr-1" /> Edit
                            </Button>
                          )}
                          {status === "scheduled" && (
                            <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 h-7 text-xs"
                              onClick={() => handleUpdateMeetingStatus(meeting.meeting_id, "checked_in")}
                              data-testid={`checkin-${meeting.meeting_id}`}>
                              <CheckCircle className="w-3 h-3 mr-1" /> Check In
                            </Button>
                          )}
                          {(status === "scheduled" || status === "checked_in") && (
                            <Button size="sm" variant="outline" className="text-green-600 border-green-200 hover:bg-green-50 h-7 text-xs"
                              onClick={() => handleUpdateMeetingStatus(meeting.meeting_id, "completed")}
                              data-testid={`complete-${meeting.meeting_id}`}>
                              <CheckCircle className="w-3 h-3 mr-1" /> Complete
                            </Button>
                          )}
                          {status === "scheduled" && (
                            <Button size="sm" variant="outline" className="text-red-500 border-red-200 hover:bg-red-50 h-7 text-xs"
                              onClick={() => {
                                const reason = prompt("Reason for cancellation (optional):");
                                handleUpdateMeetingStatus(meeting.meeting_id, "cancelled", reason);
                              }}
                              data-testid={`cancel-${meeting.meeting_id}`}>
                              <Ban className="w-3 h-3 mr-1" /> Cancel
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Events */}
        <Card className="border-neutral-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold">Upcoming Events</CardTitle>
          </CardHeader>
          <CardContent>
            {events.length === 0 ? (
              <div className="text-center py-4">
                <Calendar className="w-10 h-10 text-neutral-300 mx-auto mb-2" />
                <p className="text-neutral-500 text-sm mb-3">No events</p>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => setShowWorkshopDialog(true)}
                  className="border-neutral-300"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Create Event
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {events.slice(0, 4).map((event) => (
                  <div key={event.workshop_id} className="p-3 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition-colors group">
                    <div className="flex items-start justify-between mb-1">
                      <p className="font-medium text-neutral-900 text-sm">{event.title}</p>
                      <div className="flex items-center space-x-1">
                        {event.meeting_link && (
                          <a href={event.meeting_link} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 p-1">
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => setShowEventsDialog(true)}
                          title="Edit Event"
                        >
                          <Edit2 className="w-3 h-3 text-neutral-500" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center text-xs text-neutral-500 space-x-2">
                      <span>{event.date}</span>
                      <span>•</span>
                      <span>{event.time}</span>
                      {event.location && (
                        <>
                          <span>•</span>
                          <span>{event.location}</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-2 border-neutral-300"
                  onClick={() => setShowEventsDialog(true)}
                >
                  <Settings className="w-3 h-3 mr-2" />
                  Manage All Events
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Settings Status */}
        {!user?.meeting_link && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <p className="text-sm font-medium text-amber-800 mb-2">Setup Your Meeting Link</p>
              <p className="text-xs text-amber-700 mb-3">
                Add your Zoom/Meet link so it's automatically included in meeting invitations.
              </p>
              <Button 
                size="sm"
                onClick={() => setShowSettingsDialog(true)}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Settings className="w-3 h-3 mr-1" />
                Add Meeting Link
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Team Management */}
        <Card className="border-neutral-200">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center">
                <Shield className="w-5 h-5 mr-2 text-purple-500" />
                Team
              </CardTitle>
              <Button 
                size="sm"
                onClick={() => setShowAddConsultantDialog(true)}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="add-consultant-btn"
              >
                <UserPlus className="w-4 h-4 mr-1" />
                Add Consultant
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {teamMembers.length === 0 ? (
              <p className="text-neutral-500 text-sm text-center py-4">No team members</p>
            ) : (
              <div className="space-y-2">
                {teamMembers.map((member) => (
                  <div 
                    key={member.user_id}
                    className="flex items-center space-x-3 p-3 bg-neutral-50 rounded-lg"
                  >
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                      <span className="text-purple-700 font-semibold text-sm">
                        {member.name?.charAt(0) || "?"}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-neutral-900 text-sm truncate">{member.name}</p>
                      <p className="text-xs text-neutral-500 truncate">{member.email}</p>
                    </div>
                    {member.user_id === user?.user_id ? (
                      <Badge className="bg-purple-100 text-purple-700 text-xs">You</Badge>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-red-600 hover:text-red-800 hover:bg-red-50"
                        onClick={() => { setConsultantToDelete(member); setShowDeleteConsultantDialog(true); }}
                        title="Remove consultant"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>

    {/* Edit/Reschedule Meeting Dialog */}
    <Dialog open={!!editingMeeting} onOpenChange={(open) => { if (!open) setEditingMeeting(null); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Reschedule Meeting</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs text-neutral-500">Title</Label>
            <Input value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-neutral-500">Date</Label>
              <Input type="date" value={editForm.date} onChange={e => setEditForm({ ...editForm, date: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-neutral-500">Time</Label>
              <Input type="time" value={editForm.time} onChange={e => setEditForm({ ...editForm, time: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="text-xs text-neutral-500">Duration (minutes)</Label>
            <Input type="number" value={editForm.duration_minutes} onChange={e => setEditForm({ ...editForm, duration_minutes: parseInt(e.target.value) || 60 })} />
          </div>
          <Button onClick={handleSaveReschedule} disabled={savingEdit} className="w-full bg-[#333333] text-white">
            {savingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
